import type { StoreAuthorizationPolicy } from 'services/api/admin';

/**
 * 各平台可选站点全集（与站点选择步骤的常量保持一致），
 * 仅用于「平台整体是否可授权」的判定；单站点是否可选由后端下发的 authorizedRegions 决定。
 */
const TIKTOK_LOCAL_REGIONS: General.UpperCaseRegion[] = [
  'ID',
  'VN',
  'TH',
  'MY',
  'SG',
  'PH',
  'GB',
  'IE',
  'ES',
  'IT',
  'DE',
  'FR',
  'BR',
  'MX',
  'JP',
  'PL',
  'NL',
  'BE',
  'CZ',
  'AT',
  'GR',
  'PT',
  'HU',
];

const MERCADO_REGIONS: General.UpperCaseRegion[] = [
  'BR',
  'MX',
  'AR',
  'CO',
  'CL',
  'BO',
  'CR',
  'DO',
  'EC',
  'GT',
  'HN',
  'NI',
  'PA',
  'PY',
  'PE',
  'SV',
  'UY',
  'VE',
];

const AUTH_SCOPE_PLATFORM_REGIONS: Partial<Record<General.Platform, General.UpperCaseRegion[]>> = {
  shopee: ['PH', 'TH', 'ID', 'MY', 'VN', 'SG', 'BR', 'MX', 'CO', 'PL', 'ES', 'CL', 'FR', 'AR'],
  lazada: ['SG', 'TH', 'MY', 'PH', 'VN', 'ID'],
  tiktok: TIKTOK_LOCAL_REGIONS,
  mercado: MERCADO_REGIONS,
  kwai: ['BR'],
};

/** 单站点是否可授权；policy 未加载完成时一律视为不可选（与 perseus 口径一致） */
export function authScopeRegionAllowed(
  policy: StoreAuthorizationPolicy | undefined,
  platform: General.Platform,
  region: string,
): boolean {
  if (!policy) return false;
  // 后端字段宽容：authorizedRegions null/缺省 = 不限站点；exemptPlatforms 缺省视为空
  if (policy.authorizedRegions == null) return true;
  if (policy.exemptPlatforms?.includes(platform.toUpperCase())) return true;
  // Shopee 跨境店铺走 CNSC，无站点维度
  if (region === 'CNSC') return true;
  return policy.authorizedRegions.includes(platform === 'tiktok_us' ? 'US' : region);
}

/**
 * 平台整体是否可授权：无站点维度的平台不受限；
 * 有站点维度的平台至少要有一个可接入站点，跨境批量授权才有意义。
 */
export function authScopePlatformAllowed(
  policy: StoreAuthorizationPolicy | undefined,
  platform: General.Platform,
): boolean {
  if (!policy) return false;
  if (platform === 'tiktok_us') return authScopeRegionAllowed(policy, platform, 'US');
  const regions = AUTH_SCOPE_PLATFORM_REGIONS[platform];
  if (!regions) return true;
  return regions.some((region) => authScopeRegionAllowed(policy, platform, region));
}

/** 站点选项中被当前套餐限制（需置灰）的部分 */
export function authScopeDisabledRegions(
  policy: StoreAuthorizationPolicy | undefined,
  platform: General.Platform,
  options: General.UpperCaseRegion[],
): General.UpperCaseRegion[] {
  return options.filter((region) => !authScopeRegionAllowed(policy, platform, region));
}
